/**
 * Interactive Reading player — EAA-1755.
 *
 * Single-view learner player (no mode tabs): one reading card shows the passage
 * with a play/read button (AI model audio or browser-TTS fallback, words
 * highlighting in sync). When auto-grading is enabled, a record button appears
 * inline next to play, and the learner recording plays back below the card.
 *
 * Pronunciation scoring is performed by the async grader (`scorer.ts`) which
 * ContentPlayer awaits on submit and persists; this player just renders the
 * resulting overall score + per-word colour feedback from the `result` prop.
 */

import * as React from 'react';
import { cn, CircleIconButton, AudioPlayer, type SyncedWordScore } from '@ai-content-hub/content-kit';
import { InteractiveAudioReader } from '@ai-content-hub/content-elements';
import { isCorrectAnswerRevealed, useLocale, type GradingResult } from '@ai-content-hub/content-core';
import { Mic, Square } from 'lucide-react';
import type {
    InteractiveReadingAnswer,
    InteractiveReadingConfig,
    InteractiveReadingData,
} from './schema';
import { defaultInteractiveReadingI18n } from './schema';
import { normalizeReadingScore } from './grading';

/** Gain applied to the mic signal so quiet recordings come through louder. */
const RECORD_GAIN = 2.5;

export interface InteractiveReadingPlayerProps {
    data: InteractiveReadingData;
    answer?: InteractiveReadingAnswer;
    onAnswer?: (answer: InteractiveReadingAnswer) => void;
    disabled?: boolean;
    config?: InteractiveReadingConfig;
    /** Grading result populated by ContentPlayer after submit (score + details). */
    result?: GradingResult;
    className?: string;
}

export const InteractiveReadingPlayer: React.FC<InteractiveReadingPlayerProps> = ({
    data,
    answer,
    onAnswer,
    disabled = false,
    config,
    result,
    className,
}) => {
    // EAA-1755: merge the content-type default i18n under the stored config so
    // newly added keys (recordError, yourScore…) resolve even for content saved
    // before the key existed; author-customised strings still win.
    const i18nConfig = React.useMemo(() => {
        const stored = config?.i18n;
        return {
            defaultLanguage: stored?.defaultLanguage ?? 'en',
            languages: {
                en: { ...defaultInteractiveReadingI18n.en, ...(stored?.languages?.en ?? {}) },
                vi: { ...defaultInteractiveReadingI18n.vi, ...(stored?.languages?.vi ?? {}) },
            },
        };
    }, [config?.i18n]);
    const { t } = useLocale({ config: i18nConfig });

    const [recording, setRecording] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
    const chunksRef = React.useRef<Blob[]>([]);

    const stimulus = data.stimulus;
    const showPractice = data.autoGrade;
    const recordingUrl = answer?.recordingDataUrl;
    const passingThreshold = config?.scoring?.passingThreshold ?? 0.7;

    // Score comes from the grader's result (persisted by ContentPlayer). Prefer the
    // raw SpeechAce body for per-word detail; fall back to the overall number.
    const score = React.useMemo(() => {
        // ELMS-2370: hide answer => no score card / per-word colouring.
        if (!result || !showPractice || !isCorrectAnswerRevealed(config)) return null;
        const raw = result.details?.scoreResult as Record<string, unknown> | undefined;
        if (raw) return normalizeReadingScore(raw, passingThreshold);
        if (typeof result.score === 'number') {
            return {
                overall: Math.round(result.score),
                passed: result.isCorrect ?? result.score / 100 >= passingThreshold,
                words: [],
            };
        }
        return null;
    }, [result, showPractice, passingThreshold, config]);

    const startRecording = async () => {
        setError(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                // Ask the browser for auto-gain + cleanup so quiet mics come through louder.
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });

            // EAA-1755: boost the captured volume through a Web Audio gain stage
            // (learner recordings were too quiet). Falls back to the raw stream
            // when AudioContext is unavailable.
            const AudioCtor =
                window.AudioContext ??
                (window as unknown as { webkitAudioContext?: typeof AudioContext })
                    .webkitAudioContext;
            let recordStream: MediaStream = stream;
            let audioCtx: AudioContext | null = null;
            if (AudioCtor) {
                audioCtx = new AudioCtor();
                const source = audioCtx.createMediaStreamSource(stream);
                const gainNode = audioCtx.createGain();
                gainNode.gain.value = RECORD_GAIN;
                const destination = audioCtx.createMediaStreamDestination();
                source.connect(gainNode);
                gainNode.connect(destination);
                recordStream = destination.stream;
            }

            const recorder = new MediaRecorder(recordStream);
            chunksRef.current = [];
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };
            recorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
                const reader = new FileReader();
                reader.onloadend = () => {
                    // Persist the recording as the answer; the grader scores it on submit.
                    onAnswer?.({ ...answer, recordingDataUrl: reader.result as string });
                };
                reader.readAsDataURL(blob);
                stream.getTracks().forEach((track) => track.stop());
                if (audioCtx) void audioCtx.close();
            };
            recorder.start();
            mediaRecorderRef.current = recorder;
            setRecording(true);
        } catch {
            setError(t('recordError'));
        }
    };

    const stopRecording = () => {
        mediaRecorderRef.current?.stop();
        setRecording(false);
    };

    // EAA-1755: the record control lives next to the play button inside the
    // reading card (via the reader's controlsSlot); only when auto-grading is on.
    const recordControl = showPractice ? (
        recording ? (
            <CircleIconButton
                variant="destructive"
                onClick={stopRecording}
                aria-label={t('stop')}
                className="interactive-reading-player-stop animate-pulse"
            >
                <Square className="h-5 w-5" />
            </CircleIconButton>
        ) : (
            <CircleIconButton
                variant="outline"
                onClick={startRecording}
                disabled={disabled}
                aria-label={recordingUrl ? t('recordAgain') : t('record')}
                className="interactive-reading-player-record"
            >
                <Mic className="h-5 w-5" />
            </CircleIconButton>
        )
    ) : undefined;

    // Per-word colour feedback + hover detail once a score is in (empty until graded).
    const wordScores: SyncedWordScore[] | undefined = score?.words.length
        ? score.words.map((w) => ({
              word: w.word,
              bucket: w.bucket,
              score: w.quality,
              phones: w.phones,
          }))
        : undefined;

    return (
        <div className={cn('interactive-reading-player flex flex-col gap-4', className)}>
            {data.description && (
                <p className="interactive-reading-player-instructions text-sm text-muted-foreground">
                    {data.description}
                </p>
            )}

            <InteractiveAudioReader
                text={data.text}
                audioUrl={stimulus?.audioUrl}
                wordTimestamps={stimulus?.wordTimestamps}
                wordScores={wordScores}
                scoreLabel={t('wordScore')}
                disabled={disabled}
                labels={{ play: t('play'), pause: t('pause'), noAudio: t('noAudioYet') }}
                controlsSlot={recordControl}
                className="interactive-reading-player-reader"
            />

            {showPractice && recordingUrl && !recording && (
                <div className="interactive-reading-player-playback flex flex-col items-center gap-2">
                    <AudioPlayer src={recordingUrl} />
                </div>
            )}

            {score && (
                <div
                    className={cn(
                        'interactive-reading-player-score flex items-center justify-center gap-3 rounded-sm border p-3',
                        score.passed
                            ? 'border-success/40 bg-success/10'
                            : 'border-destructive/40 bg-destructive/10'
                    )}
                >
                    <span className="text-sm text-muted-foreground">{t('yourScore')}</span>
                    <span
                        className={cn(
                            'text-2xl font-semibold',
                            score.passed ? 'text-success' : 'text-destructive'
                        )}
                    >
                        {score.overall}
                        <span className="text-base font-normal text-muted-foreground">/100</span>
                    </span>
                </div>
            )}

            {error && (
                <p className="interactive-reading-player-error text-center text-sm text-destructive">
                    {error}
                </p>
            )}
        </div>
    );
};

export default InteractiveReadingPlayer;
